import React, { useState, useEffect } from "react";
import { Box, Autocomplete, TextField } from "@mui/material";
import { useUser } from "../UserContext";
import axios from "axios";

interface account {
  id: number;
  account: string;
  address: string;
  city: string;
  state_code: string;
  zip_code: string;
}

interface SearchBarProps {
  value?: account | null;
  onChange?: (account: account | null) => void;
  onSelectaccount: (accountId: number) => void;
  boxSize?: "small" | "medium" | "large";
}

const SearchBarAccount: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSelectaccount,
  boxSize = "medium",
}) => {
  const [accounts, setaccounts] = useState<account[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedaccount, setSelectedaccount] = useState<account | null>(
    value || null
  );
  const { currentUser, selectedBrandId } = useUser();

  useEffect(() => {
    const fetchaccounts = async () => {
      if (currentUser) {
        setLoading(true);
        try {
          let stateCodes: string[] = [];

          // Determine state codes based on user role
          if (
            currentUser.role === "Brand" &&
            selectedBrandId &&
            currentUser.brands_json
          ) {
            const selectedBrand = currentUser.brands_json.find(
              (brand: { brand_id: number }) =>
                brand.brand_id === selectedBrandId
            );
            if (selectedBrand?.markets) {
              stateCodes = selectedBrand.markets;
            }
          } else if (
            currentUser.role === "Supplier" &&
            currentUser.brands_json
          ) {
            // Combine all unique markets from all brands
            stateCodes = Array.from(
              new Set(
                currentUser.brands_json.flatMap(
                  (brand: { markets: string[] }) => brand.markets
                )
              )
            );
          }

          const response = await axios.get<account[]>(
            `${import.meta.env.VITE_API_URL}/accounts/accounts`,
            {
              params: {
                role: currentUser.role,
                state_codes: stateCodes.join(","),
              },
            }
          );
          setaccounts(response.data);
          console.log(response.data);
        } catch (error) {
          console.error("Error fetching accounts:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchaccounts();
  }, [currentUser, selectedBrandId]);

  const handleAccountSelect = (account: account | null) => {
    setSelectedaccount(account);
    if (account) {
      onSelectaccount(account.id);
      if (onChange) {
        onChange(account);
      }
    } else {
      onSelectaccount(0);
    }
  };

  return (
    <Box
      sx={{
        width:
          boxSize === "small" ? "50%" : boxSize === "medium" ? "90%" : "100%",
      }}
    >
      <Autocomplete
        value={selectedaccount}
        onChange={(_, newValue) => {
          handleAccountSelect(newValue);
        }}
        options={accounts}
        getOptionLabel={(option) =>
          `${option.account} - ${option.city}, ${option.state_code}`
        }
        filterOptions={(options, { inputValue }) =>
          inputValue
            ? options
                .filter((option) =>
                  option.account
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
                .slice(0, 5)
            : []
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={"Search Accounts"}
            size={"small"}
            placeholder={"Type to search accounts..."}
          />
        )}
        loading={loading}
        loadingText="Loading accounts..."
        noOptionsText="No accounts found"
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ width: "100%" }}
      />
    </Box>
  );
};

export default SearchBarAccount;
